/**
 * editor2/modals/StaffSelectModal.tsx
 * DayListと同じUIのスタッフ選択モーダル + スポットスタッフ対応
 */
import { isSystemAccount } from '../../../utils/staffPriority';
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
} from '@mui/material';

interface StaffMember {
  id: number | string;
  name?: string;
  staff_name?: string;
  bg_color?: string;
  text_color?: string;
  active?: number;
  is_deleted?: boolean;
  [key: string]: any;
}

interface PinnedStaffUser {
  line_user_id: string;
  staffName: string;
  _isPinnedStaff?: boolean;
  [key: string]: any;
}

interface StaffSelectModalProps {
  open: boolean;
  title: string;
  staffList: StaffMember[];
  currentStaffId: any;
  onSelect: (staffId: string | number | null) => void;
  onClose: () => void;
  pinnedStaffUsers?: PinnedStaffUser[];
}

export default function StaffSelectModal({
  open,
  title,
  staffList,
  currentStaffId,
  onSelect,
  onClose,
  pinnedStaffUsers = [],
}: StaffSelectModalProps) {
  // DayListと同じフィルタリング
  const filteredStaff = staffList.filter((staff) => {
    // システムアカウントは選ばせない（ID 決め打ちは店舗ごとの採番差で漏れる）
    if (isSystemAccount(staff)) return false;
    if (staff.active === 1 && !staff.is_deleted) return true;
    if (String(staff.id) === String(currentStaffId)) return true;
    return false;
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ style: { borderRadius: 12 } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>{title}を選択</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid {...({ container: true } as any)} spacing={1}>
          {/* 未指定 */}
          <Grid {...({ item: true, xs: 4 } as any)}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => onSelect(null)}
              sx={{
                height: '100%',
                borderColor: '#bdbdbd',
                color: '#757575',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              未指定
            </Button>
          </Grid>
          {/* スタッフリスト */}
          {filteredStaff.map((staff) => {
            const isCurrentStaff = String(staff.id) === String(currentStaffId);
            const isInactive = staff.active !== 1 || staff.is_deleted;
            const displayName = staff.staff_name || staff.name || String(staff.id);

            return (
              <Grid {...({ item: true, xs: 4 } as any)} key={staff.id}>
                <Button
                  fullWidth
                  variant={staff.bg_color ? 'contained' : 'outlined'}
                  onClick={() => onSelect(staff.id)}
                  sx={{
                    height: '100%',
                    opacity: isInactive ? 0.6 : 1,
                    backgroundColor: staff.bg_color || undefined,
                    color: staff.text_color || undefined,
                    borderColor: staff.bg_color
                      ? 'transparent'
                      : staff.text_color || undefined,
                    boxShadow: isCurrentStaff
                      ? '0 0 0 2px #1976d2'
                      : undefined,
                    '&:hover': {
                      backgroundColor: staff.bg_color
                        ? `${staff.bg_color}dd`
                        : undefined,
                      filter: 'brightness(0.9)',
                    },
                  }}
                >
                  {displayName}
                </Button>
              </Grid>
            );
          })}
        </Grid>

        {/* スポットスタッフ（PU/DOのみ） */}
        {pinnedStaffUsers.length > 0 && (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 2, mb: 0.5, display: 'block' }}
            >
              スポットスタッフ
            </Typography>
            <Grid {...({ container: true } as any)} spacing={1}>
              {pinnedStaffUsers.map((pStaff) => (
                <Grid {...({ item: true, xs: 4 } as any)} key={pStaff.line_user_id}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => onSelect(pStaff.line_user_id as any)}
                    sx={{
                      height: '100%',
                      backgroundColor: '#e8f5e9',
                      borderColor: '#06C755',
                      color: '#2e7d32',
                      '&:hover': {
                        backgroundColor: '#c8e6c9',
                      },
                    }}
                  >
                    {pStaff.staffName}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">
          キャンセル
        </Button>
      </DialogActions>
    </Dialog>
  );
}
